/**
 * NCR list filter panel — Quality → NCRs.
 *
 * CR-6451: one shell for both views, matching the Deliveries tab — the header
 * (Filter NCRs, Refresh, Export, Filters toggle with active count) stays
 * visible when the panel is collapsed; only the sections and the Clear all /
 * Apply filters footer hide. The supplier view renders the design's four
 * sections (`NCRFilterSupplierLayout`); the customer view keeps its CR-6444
 * body (`NCRFilterSections`). Only the section body differs.
 *
 * The panel edits a DRAFT (`values`) and sends it on Apply — the page owns
 * the draft/applied split (see `useNCRFilterDraft`). This file keeps the
 * option lists and the dropdown data loading.
 *
 * Quick date range chips (Last 12 Months / This Month / Last Month / Last
 * Quarter / This Year / Last Year) mirror DashboardFilters.tsx. Selecting a
 * chip rewrites dateFrom / dateTo; manual edits clear the active chip.
 */
import { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Download, Filter, RefreshCw, Search, X } from 'lucide-react';
import { NCRFilterSections } from './NCRFilterSections';
import { NCRFilterSupplierLayout } from './NCRFilterSupplierLayout';
import { coreAPI, suppliersAPI, purchasingAPI, fetchAllPages } from '../../lib/api';
import type { CompanyGroup, Site, SupplierProfile, Warehouse } from '../../lib/api';
import type { NCRFiltersState } from './ncrFilterParams';
import { useNCRFilterCascades, type NCRFilterCascadeOptions } from './useNCRFilterCascades';
import { useAuth } from '../../contexts/AuthContext';
import { useViewKey } from '../../contexts/ViewContext';

export type { NCRFiltersState } from './ncrFilterParams';

/**
 * NCR liability options mirror NonConformanceReport.LIABILITY_TYPE_CHOICES
 * on the backend model (IntegerField, value-keyed).
 */
export const LIABILITY_OPTIONS: { value: string; label: string }[] = [
  { value: '0', label: 'Not Determined' },
  { value: '1', label: 'Supplier Liability' },
  { value: '2', label: 'Customer Liability' },
  { value: '3', label: 'Shared Liability' },
  { value: '4', label: 'No Liability' },
];

/** Credit/debit claim workflow statuses (NCRCreditProcess.status). */
export const CLAIM_STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: 'requested', label: 'Requested' },
  { value: 'under_review', label: 'Under Review' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'processed', label: 'Processed' },
];

/** NCR workflow statuses (NonConformanceReport.status). */
export const NCR_STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: 'open', label: 'Open' },
  { value: 'draft', label: 'Draft' },
  { value: 'created', label: 'Created' },
  { value: 'submitted', label: 'Submitted (no 8D)' },
  { value: 'ca_submitted', label: 'CA Submitted' },
  { value: 'car_submitted', label: 'CAR Submitted' },
  { value: 'eightd_complete', label: '8D Complete' },
  { value: 'closed', label: 'Closed' },
  { value: 'deleted', label: 'Deleted' },
];

export interface NCRFiltersBarProps {
  /** E4: what this customer calls its own NCR number. */
  customerReferenceLabel?: string;
  /** The draft the panel edits. */
  values: NCRFiltersState;
  onChange: (next: Partial<NCRFiltersState>) => void;
  /** Push the draft into the applied filters. */
  onApply: () => void;
  onClear: () => void;
  /** Collapsed state. The header stays visible when collapsed. */
  expanded: boolean;
  onShowFilters: () => void;
  onHideFilters: () => void;
  onRefresh: () => void;
  onExport: () => void;
  exporting?: boolean;
  /** Number of filters currently applied, shown as a badge in the header. */
  activeFilterCount?: number;
  /** CR-6451: render the supplier-view layout from the supplied design. */
  supplierLayout?: boolean;
  /** CR-6451: false on the supplier portal — see NCRFilterSections. */
  showStrategicFilter?: boolean;
  /** CR-7349: the warehouses this panel loaded, reported up so the register's
   *  Warehouse column funnel offers the same options without a second fetch.
   *  Called once per load, and with [] when the request fails. */
  onWarehouseOptionsLoaded?: (warehouses: Warehouse[]) => void;
}

interface CommodityOption { id: number; name: string; commodity_group?: number | null; }

/** Format a Date as YYYY-MM-DD in local time — avoids UTC shift on BST midnight. */
function formatLocalDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

type QuickRangeId = 'last12Months' | 'thisMonth' | 'lastMonth' | 'lastQuarter' | 'thisYear' | 'lastYear';

/**
 * The list's default window: a rolling 12 months up to today, open-ended
 * (`dateTo` = ''). This is what the NCR list loads with, so the
 * "Last 12 Months (Default)" chip applies exactly the same range — the
 * chip used to apply the previous 12 COMPLETE months (to last month end),
 * which silently hid anything raised this month.
 */
export function getRollingTwelveMonthsRange(): { dateFrom: string; dateTo: string } {
  const today = new Date();
  return {
    dateFrom: formatLocalDate(new Date(today.getFullYear() - 1, today.getMonth(), today.getDate())),
    dateTo: '',
  };
}

function getQuickRangeDates(range: QuickRangeId): { dateFrom: string; dateTo: string } {
  const today = new Date();
  switch (range) {
    case 'last12Months':
      return getRollingTwelveMonthsRange();
    case 'thisMonth':
      return {
        dateFrom: formatLocalDate(new Date(today.getFullYear(), today.getMonth(), 1)),
        dateTo: formatLocalDate(new Date(today.getFullYear(), today.getMonth() + 1, 0)),
      };
    case 'lastMonth':
      return {
        dateFrom: formatLocalDate(new Date(today.getFullYear(), today.getMonth() - 1, 1)),
        dateTo: formatLocalDate(new Date(today.getFullYear(), today.getMonth(), 0)),
      };
    case 'lastQuarter': {
      const lq = Math.floor(today.getMonth() / 3) - 1;
      const lqy = lq < 0 ? today.getFullYear() - 1 : today.getFullYear();
      const aq = lq < 0 ? 3 : lq;
      return {
        dateFrom: formatLocalDate(new Date(lqy, aq * 3, 1)),
        dateTo: formatLocalDate(new Date(lqy, (aq + 1) * 3, 0)),
      };
    }
    case 'thisYear':
      return {
        dateFrom: formatLocalDate(new Date(today.getFullYear(), 0, 1)),
        dateTo: formatLocalDate(new Date(today.getFullYear(), today.getMonth() + 1, 0)),
      };
    case 'lastYear':
      return {
        dateFrom: formatLocalDate(new Date(today.getFullYear() - 1, 0, 1)),
        dateTo: formatLocalDate(new Date(today.getFullYear() - 1, 11, 31)),
      };
  }
}

const QUICK_RANGE_LABELS: Record<QuickRangeId, string> = {
  last12Months: 'Last 12 Months (Default)',
  thisMonth: 'This Month',
  lastMonth: 'Last Month',
  lastQuarter: 'Last Quarter',
  thisYear: 'This Year',
  lastYear: 'Last Year',
};

/** CR-6451: the supplier design labels the chips plainly. */
const SUPPLIER_QUICK_RANGE_LABELS: Record<QuickRangeId, string> = {
  ...QUICK_RANGE_LABELS,
  last12Months: 'Last 12 Months',
};

const QUICK_RANGE_ORDER: QuickRangeId[] = ['last12Months', 'thisMonth', 'lastMonth', 'lastQuarter', 'thisYear', 'lastYear'];

interface DivisionOption {
  id: number;
  name: string;
}

/**
 * CR-6444: the Division list for users who cannot read the company-groups
 * endpoint (it is Helix-admin only and answers 403 to customers, which left
 * the Division control hidden for exactly the users the ticket is about).
 * Every site row already names its group, so the distinct groups of the
 * sites the user can see are their divisions — the same derivation the
 * Calendar and Dashboard panels use.
 */
export function deriveDivisionsFromSites(sites: Site[]): DivisionOption[] {
  const byId = new Map<number, string>();
  for (const s of sites) {
    const id = s.group_id ?? null;
    const name = s.group_name;
    if (id && name && !byId.has(id)) byId.set(id, name);
  }
  return Array.from(byId, ([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
}

export default function NCRFiltersBar({
  customerReferenceLabel,
  values, onChange, onApply, onClear, expanded, onShowFilters, onHideFilters,
  onRefresh, onExport, exporting = false, activeFilterCount = 0,
  supplierLayout = false, showStrategicFilter = true, onWarehouseOptionsLoaded,
}: NCRFiltersBarProps) {
  const [divisions, setDivisions] = useState<DivisionOption[]>([]);
  const [supplierGroups, setSupplierGroups] = useState<CompanyGroup[]>([]);
  const [sites, setSites] = useState<Site[]>([]);
  const [suppliers, setSuppliers] = useState<SupplierProfile[]>([]);
  const [commodityGroups, setCommodityGroups] = useState<CommodityOption[]>([]);
  const [commodityTypes, setCommodityTypes] = useState<CommodityOption[]>([]);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  // CR-6444: supplier company id → customer ids it serves, for the Site →
  // Supplier Site cascade. Customer layout only; the supplier portal's
  // "Supplier Site" list is the user's own sites and has no such cascade.
  const [supplierCustomers, setSupplierCustomers] = useState<Record<number, number[]>>({});

  // /core/company-groups/ is IsHelixUser. Asking for it as anyone else is a
  // guaranteed 403 — swallowed by allSettled below, but it still put a red
  // error in every customer admin's console on each Quality page load. The
  // Division dropdown does not need it: it falls back to the groups named by
  // the sites the user can see, which is what a non-Helix viewer has always
  // actually been shown.
  const { user } = useAuth();
  const viewKey = useViewKey(); // supplier options follow the site switcher
  const isHelixUser = Boolean(user && (user.platform === 'HELIX' || user.company_type === 'helix'));

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Promise.allSettled so a single 403 (e.g. /core/sites/ has a
      // tenant-scope gate that rejects some viewers) doesn't blank
      // every other dropdown.
      // Walk pages — the backend clamps page_size, so one request silently truncated.
      const [divisionsRes, groupsRes, sitesRes, suppliersRes, cGroupsRes, cTypesRes, warehousesRes, supplierLinksRes] = await Promise.allSettled([
        // CR-6444: divisions (company groups) for the Division → Site cascade,
        // for the only role allowed to read the list.
        isHelixUser
          ? coreAPI.getCompanyGroups({ page_size: 100, company_type: 'customer' })
          : Promise.resolve({ data: { results: [] as CompanyGroup[] } }),
        coreAPI.getSupplierGroupsForFilter(),
        // CR-6451: /core/sites/ is customer-scoped and 403s for a supplier, so
        // the supplier layout reads the customer sites it serves from the
        // accessible endpoint instead (same source the ASN and Deliveries
        // supplier panels use). Customers keep the full sites list.
        supplierLayout
          ? coreAPI.getAccessibleSites().then((res) => (Array.isArray(res.data) ? res.data : ((res.data as unknown as { results?: Site[] }).results || [])) as Site[])
          : fetchAllPages<Site>((page) => coreAPI.getSites({ page_size: 100, page })),
        // The supplier cap is 1000 a page; a newer load (site switch) stops
        // this walk rather than fetching a list nobody will see.
        fetchAllPages<SupplierProfile>((page) => (cancelled
          ? Promise.reject(new Error('Superseded by a newer filter load'))
          : suppliersAPI.getProfiles({ minimal: true, page_size: 1000, page }))),
        fetchAllPages<CommodityOption>((page) => purchasingAPI.getCommodityGroups({ page_size: 100, page })),
        fetchAllPages<CommodityOption>((page) => purchasingAPI.getCommodityTypes({ page_size: 100, page })),
        // CR-6451: the plain warehouse list is Helix-admin only (403 otherwise);
        // the supplier layout reads the accessible, site-scoped list instead.
        supplierLayout
          ? coreAPI.getAccessibleWarehouses().then((res) => (Array.isArray(res.data) ? res.data : []) as Warehouse[])
          : fetchAllPages<Warehouse>((page) => coreAPI.getWarehouses({ page_size: 100, page })),
        // CR-6444: which customers each supplier serves (the `customers` field
        // the ASN and Pricing panels already read for their Site → Supplier
        // cascade). Skipped on the supplier layout.
        supplierLayout
          ? Promise.resolve({ data: { results: [] as Array<{ id: number; customers?: number[] | null }> } })
          : Promise.resolve().then(() => coreAPI.getSupplierSitesForFilter()),
      ]);
      if (cancelled) return;
      if (supplierLinksRes.status === 'fulfilled') {
        const links: Record<number, number[]> = {};
        for (const c of supplierLinksRes.value.data?.results || []) {
          if (Array.isArray(c.customers)) links[c.id] = c.customers;
        }
        setSupplierCustomers(links);
      }
      const pickResults = <T,>(res: PromiseSettledResult<{ data: { results?: T[] } | T[] }>): T[] => {
        if (res.status !== 'fulfilled') return [];
        const d = res.value.data as { results?: T[] } | T[];
        if (Array.isArray(d)) return d;
        return (d.results as T[]) || [];
      };
      // fetchAllPages resolves to the row array itself — no `.data.results` unwrap.
      const pickAll = <T,>(res: PromiseSettledResult<T[]>): T[] =>
        res.status === 'fulfilled' ? res.value : [];
      const logReject = (label: string, res: PromiseSettledResult<unknown>) => {
        if (res.status === 'rejected') {
          console.warn(`NCR filters: ${label} dropdown unavailable —`, (res.reason as { message?: string })?.message || res.reason);
        }
      };
      // CR-6444: the full division list when the endpoint allows it (Helix
      // admins); otherwise the divisions of the sites the user can see.
      const apiDivisions = pickResults<DivisionOption>(divisionsRes);
      const siteRows = pickAll(sitesRes);
      setDivisions(apiDivisions.length > 0 ? apiDivisions : deriveDivisionsFromSites(siteRows));
      setSupplierGroups(pickResults<CompanyGroup>(groupsRes));
      setSites(siteRows);
      // Each page is cached on its own, so one fetched later can repeat a row.
      setSuppliers(Array.from(new Map(pickAll(suppliersRes).map((s) => [s.company_id ?? s.company, s])).values()));
      setCommodityGroups(pickAll(cGroupsRes));
      setCommodityTypes(pickAll(cTypesRes));
      const loadedWarehouses = pickAll(warehousesRes) as Warehouse[];
      setWarehouses(loadedWarehouses);
      onWarehouseOptionsLoaded?.(loadedWarehouses);
      // A 403 on the division endpoint is the normal case for customers —
      // only worth a warning when the site-derived list is empty too.
      if (deriveDivisionsFromSites(siteRows).length === 0) logReject('divisions', divisionsRes);
      logReject('supplier groups', groupsRes);
      logReject('sites', sitesRes);
      logReject('suppliers', suppliersRes);
      logReject('commodity groups', cGroupsRes);
      logReject('commodity types', cTypesRes);
      logReject('warehouses', warehousesRes);
      logReject('supplier links', supplierLinksRes);
    })();
    return () => { cancelled = true; };
  }, [supplierLayout, isHelixUser, viewKey]);

  // CR-6444: cascade hook provides filtered options and handlers for
  // Division → Site, Site → Warehouse, Supplier Group/Country → Supplier,
  // Commodity Group → Commodity Type cascades.
  const cascadeOptions: NCRFilterCascadeOptions = useMemo(() => ({
    divisions,
    sites,
    warehouses,
    suppliers,
    supplierGroups,
    commodityGroups,
    commodityTypes,
    supplierCustomers,
  }), [divisions, sites, warehouses, suppliers, supplierGroups, commodityGroups, commodityTypes, supplierCustomers]);

  const {
    filteredSites,
    filteredWarehouses,
    filteredSuppliers,
    filteredCommodityTypes,
    companyCountryOptions,
    supplierCountryOptions,
    handleDivisionChange,
    handleCompanyCountryChange,
    handleSiteChange,
    handleSupplierGroupChange,
    handleSupplierCountryChange,
    handleCommodityGroupChange,
  } = useNCRFilterCascades(values, cascadeOptions, onChange);

  // CR-6444: Supplier Type options - derived from company_type values in suppliers
  const supplierTypeOptions = useMemo(() => {
    const set = new Set<string>();
    suppliers.forEach((s) => {
      const companyType = (s.company_details as unknown as { company_type?: string | null } | undefined)?.company_type;
      if (companyType) set.add(companyType);
    });
    return Array.from(set).sort().map((t) => ({ value: t, label: t }));
  }, [suppliers]);

  // Derive which quick chip matches the current dateFrom/dateTo so the
  // active state highlights when the chip-set dates are still in the inputs.
  const activeQuickRange = useMemo<QuickRangeId | null>(() => {
    if (!values.dateFrom && !values.dateTo) return null;
    return QUICK_RANGE_ORDER.find(r => {
      const d = getQuickRangeDates(r);
      return d.dateFrom === values.dateFrom && d.dateTo === values.dateTo;
    }) || null;
  }, [values.dateFrom, values.dateTo]);

  const setQuickRange = (range: QuickRangeId) => {
    const { dateFrom, dateTo } = getQuickRangeDates(range);
    onChange({ dateFrom, dateTo });
  };

  const body = supplierLayout ? (
    <NCRFilterSupplierLayout
      values={values}
      onChange={onChange}
      quickRanges={QUICK_RANGE_ORDER.map((id) => ({ id, label: SUPPLIER_QUICK_RANGE_LABELS[id] }))}
      activeQuickRange={activeQuickRange}
      onQuickRange={(id) => setQuickRange(id as QuickRangeId)}
      siteOptions={sites.map((s) => ({ value: String(s.id), label: s.name }))}
      warehouseOptions={warehouses.map((w) => ({ value: String(w.id), label: w.name || w.code }))}
      supplierCountryOptions={supplierCountryOptions}
      supplierGroupOptions={supplierGroups.map((g) => ({ value: String(g.id), label: g.name }))}
      supplierSiteOptions={filteredSuppliers
        .filter((s) => (s.company_id ?? s.company) != null)
        .map((s) => ({ value: String(s.company_id ?? s.company), label: s.company_name }))}
      statusOptions={NCR_STATUS_OPTIONS}
      liabilityOptions={LIABILITY_OPTIONS}
      onApply={onApply}
    />
  ) : (    
      <NCRFilterSections
        customerReferenceLabel={customerReferenceLabel}
        values={values}
        onChange={onChange}
        quickRanges={QUICK_RANGE_ORDER.map((id) => ({ id, label: QUICK_RANGE_LABELS[id] }))}
        activeQuickRange={activeQuickRange}
        onQuickRange={(id) => setQuickRange(id as QuickRangeId)}
        divisions={divisions}
        companyCountryOptions={companyCountryOptions}
        sites={filteredSites}
        warehouses={filteredWarehouses}
        supplierGroups={supplierGroups}
        filteredSuppliers={filteredSuppliers}
        supplierCountryOptions={supplierCountryOptions}
        supplierTypeOptions={supplierTypeOptions}
        commodityGroups={commodityGroups}
        filteredCommodityTypes={filteredCommodityTypes}
        liabilityOptions={LIABILITY_OPTIONS}
        showStrategicFilter={showStrategicFilter}
        onDivisionChange={handleDivisionChange}
        onCompanyCountryChange={handleCompanyCountryChange}
        onSiteChange={handleSiteChange}
        onSupplierGroupChange={handleSupplierGroupChange}
        onSupplierCountryChange={handleSupplierCountryChange}
        onCommodityGroupChange={handleCommodityGroupChange}
      />    
  );

  return (
    <Card>
      {/* Filter header — stays visible when the panel is collapsed. */}
      <CardHeader className="py-3">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <CardTitle className="text-base text-xl">Filter NCRs</CardTitle>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={onRefresh}>
              <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
              Refresh
            </Button>
            <Button size="sm" variant="outline" onClick={onExport} disabled={exporting}>
              <Download className="h-3.5 w-3.5 mr-1.5" />
              Export
            </Button>
            <button
              onClick={expanded ? onHideFilters : onShowFilters}
              data-tour="ncr-filters"
              className={`inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-md border transition-colors ${
                expanded
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'border-border hover:bg-muted'
              }`}
            >
              <Filter className="h-4 w-4" />
              Filters
              {activeFilterCount > 0 && (
                <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                  expanded
                    ? 'bg-primary-foreground/20 text-primary-foreground'
                    : 'bg-primary text-primary-foreground'
                }`}>
                  {activeFilterCount}
                </span>
              )}
            </button>
          </div>
        </div>
      </CardHeader>
      {expanded && (
        <CardContent className="space-y-4 p-0">
          {body}
          {/* Filter footer — Clear all left, Apply filters right. */}
          <div className="flex items-center justify-between px-6 pb-4">
            <Button size="sm" variant="outline" onClick={onClear}>
              <X className="h-3.5 w-3.5 mr-1.5" />
              Clear all
            </Button>
            <Button size="sm" onClick={onApply}>
              <Search className="h-3.5 w-3.5 mr-1.5" />
              Apply filters
            </Button>
          </div>
        </CardContent>
      )}
    </Card>
  );
}
